import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tagApi } from '../lib/api'
import type { SaveTagRequest } from '../types/tag'

export function useTags() {
  return useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await tagApi.getAll()
      return response.data
    },
  })
}

export function useTagDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['tags', id],
    queryFn: async () => {
      if (!id) throw new Error('Tag ID is required')
      const response = await tagApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveTagRequest) =>
      tagApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'tags'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('tags'),
      })
    },
  })
}

export function useUpdateTag(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveTagRequest) =>
      tagApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['tags', id], data)
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'tags'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('tags'),
      })
    },
  })
}

export function useDeleteTag() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => tagApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags'] })
      queryClient.invalidateQueries({ queryKey: ['scripts', 'picker', 'tags'] })
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === 'campaigns' &&
          query.queryKey.includes('picker') &&
          query.queryKey.includes('tags'),
      })
    },
  })
}
